"use client";

import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import {
  isAddress,
  maxUint256,
  parseEther,
  parseUnits,
  zeroAddress,
} from "viem";
import { useAccount, usePublicClient, useWalletClient } from "wagmi";
import { useAuth } from "@/context/auth";
import { ERC20_ABI } from "@/contract/abi/erc20";
import { GATEWAY_ABI } from "@/contract/abi/gateway";
import { LAND_MARKET_ABI, landMarketMintArgs } from "@/contract/abi/landMarket";
import ContractAddress from "@/contract/addresses";
import { useGuardedWriteContract } from "@/hooks/auth/useGuardedWrite";
import { assertLiveSigner } from "@/hooks/checkout/assertLiveSigner";
import { useContractErrorHandler } from "@/hooks/useContractErrorHandler";
import { $api } from "@/lib/api-client";
import {
  formatTokenAmount,
  InsufficientBalanceError,
} from "@/lib/checkout/insufficientBalanceError";
import { createTransactionLogger } from "@/lib/transaction-logger";

type PurchaseNFTParams = {
  tokenId: number;
  /** Binary-tree placement target, from usePlacementResolution. */
  parentAddress: string;
  /**
   * Who referred the buyer, from usePlacementResolution -- the zero address
   * for a buyer who is not (or will never be) referred.
   */
  referrerAddress: string;
  price: { sv: number; bv: number };
  nftMarketAddress: string;
};

type PurchaseNFTResult = {
  tokenId: number;
  nftMarketAddress: string;
  userId: bigint;
  /** The wallet's ACTUAL on-chain referrer, read fresh post-tx. */
  onChainReferrer: `0x${string}` | undefined;
};

type UsePurchaseNFTOptions = {
  onSuccess?: (data: PurchaseNFTResult) => void;
  onError?: (error: Error) => void;
};

export const usePurchaseNFT = (options?: UsePurchaseNFTOptions) => {
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const updateUser = $api.useMutation("patch", "/user/me/referrer");
  const { writeContractAsync } = useGuardedWriteContract();
  const te = useTranslations("errors");
  const tc = useTranslations("checkout");
  const tp = useTranslations("checkout.purchase");
  const { handleContractError } = useContractErrorHandler();

  const mutationFn = async (params: PurchaseNFTParams) => {
    const logger = createTransactionLogger(
      "nft-purchase",
      address ?? "unknown",
    );

    try {
      logger.step("validation", {
        tokenId: params.tokenId,
        nftMarketAddress: params.nftMarketAddress,
        sv: params.price.sv,
        bv: params.price.bv,
        parentAddress: params.parentAddress,
      });

      if (!address) throw new Error("Wallet not connected");
      if (!publicClient) throw new Error("Public client not available");

      // Identity pre-flight against the WALLET itself — see assertLiveSigner.
      await assertLiveSigner({
        walletClient,
        cachedAddress: address,
        sessionWallet: useAuth.getState().user?.wallet_address,
        mismatchMessage: tc("referrer.walletMismatch"),
      });

      const {
        tokenId,
        parentAddress,
        referrerAddress,
        price,
        nftMarketAddress,
      } = params;
      if (!isAddress(parentAddress))
        throw new Error(tp("invalidReferral", { address: parentAddress }));
      if (!isAddress(referrerAddress))
        throw new Error(tp("invalidReferral", { address: referrerAddress }));

      const waitAndVerifyTransaction = async (
        txHash: `0x${string}`,
        stepName: string,
        options?: { allowZeroLogs?: boolean },
      ) => {
        logger.step(`${stepName}_confirm`, { txHash });
        const receipt = await publicClient.waitForTransactionReceipt({
          hash: txHash,
        });
        if (
          receipt.status === "reverted" ||
          (typeof receipt.status === "number" && receipt.status === 0)
        ) {
          throw new Error(`${stepName} transaction reverted`);
        }
        // Calling an address with no deployed code (a stale/misconfigured
        // contract address) is itself a trivial EVM success -- `status`
        // alone cannot tell a real purchase from a no-op that silently did
        // nothing. Every event-emitting path through here (ERC20
        // approve/transfer and NFT mint) is required to emit at least one
        // log. Log-free storage writes must opt in and verify their state
        // separately after the receipt.
        if (receipt.logs.length === 0 && !options?.allowZeroLogs) {
          throw new Error(
            `${stepName} transaction produced no on-chain effect (0 events) -- the contract address is likely misconfigured or has no code`,
          );
        }
        return receipt;
      };

      // UVM allowance
      logger.step("uvm_allowance_check");
      const currentUVMAllowance = await publicClient.readContract({
        address: ContractAddress.UVM,
        abi: ERC20_ABI,
        functionName: "allowance",
        args: [address, ContractAddress.GATEWAY],
      });

      if (currentUVMAllowance !== maxUint256) {
        logger.step("uvm_approval_request", {
          current: currentUVMAllowance.toString(),
          requesting: "maxUint256",
        });
        const uvmTxHash = await writeContractAsync({
          address: ContractAddress.UVM,
          abi: ERC20_ABI,
          functionName: "approve",
          args: [ContractAddress.GATEWAY, maxUint256],
        });
        await waitAndVerifyTransaction(uvmTxHash, "uvm_approval");

        const newUVMAllowance = await publicClient.readContract({
          address: ContractAddress.UVM,
          abi: ERC20_ABI,
          functionName: "allowance",
          args: [address, ContractAddress.GATEWAY],
        });
        if (newUVMAllowance !== maxUint256) {
          throw new Error("UVM allowance approval failed or insufficient");
        }
      }

      // USDT allowance
      logger.step("usdt_allowance_check");
      const currentUSDTAllowance = await publicClient.readContract({
        address: ContractAddress.USDT,
        abi: ERC20_ABI,
        functionName: "allowance",
        args: [address, ContractAddress.GATEWAY],
      });

      if (currentUSDTAllowance !== maxUint256) {
        logger.step("usdt_approval_request", {
          current: currentUSDTAllowance.toString(),
          requesting: "maxUint256",
        });
        const usdtTxHash = await writeContractAsync({
          address: ContractAddress.USDT,
          abi: ERC20_ABI,
          functionName: "approve",
          args: [ContractAddress.GATEWAY, maxUint256],
        });
        await waitAndVerifyTransaction(usdtTxHash, "usdt_approval");

        const newUSDTAllowance = await publicClient.readContract({
          address: ContractAddress.USDT,
          abi: ERC20_ABI,
          functionName: "allowance",
          args: [address, ContractAddress.GATEWAY],
        });
        if (newUSDTAllowance !== maxUint256) {
          throw new Error("USDT allowance approval failed or insufficient");
        }
      }

      // Final allowance verification
      logger.step("final_allowance_verify");
      const [finalUVMAllowance, finalUSDTAllowance] = await Promise.all([
        publicClient.readContract({
          address: ContractAddress.UVM,
          abi: ERC20_ABI,
          functionName: "allowance",
          args: [address, ContractAddress.GATEWAY],
        }),
        publicClient.readContract({
          address: ContractAddress.USDT,
          abi: ERC20_ABI,
          functionName: "allowance",
          args: [address, ContractAddress.GATEWAY],
        }),
      ]);

      if (finalUVMAllowance !== maxUint256) {
        throw new Error("Insufficient UVM allowance for purchase");
      }
      if (finalUSDTAllowance !== maxUint256) {
        throw new Error("Insufficient USDT allowance for purchase");
      }

      // Gateway allowance (set to maxUint256)
      logger.step("gateway_allowance_request", {
        nftMarketAddress,
        requesting: "maxUint256",
      });
      const gatewayTxHash = await writeContractAsync({
        address: ContractAddress.GATEWAY,
        abi: GATEWAY_ABI,
        functionName: "setAllowance",
        args: [nftMarketAddress as `0x${string}`, maxUint256],
      });
      await waitAndVerifyTransaction(gatewayTxHash, "gateway_allowance", {
        allowZeroLogs: true,
      });

      const gatewayAllowance = await publicClient.readContract({
        address: ContractAddress.GATEWAY,
        abi: GATEWAY_ABI,
        functionName: "getAllowance",
        args: [address, nftMarketAddress as `0x${string}`],
      });
      if (gatewayAllowance !== maxUint256) {
        throw new Error("Gateway allowance setup failed or insufficient");
      }

      // Balance check — friendly Persian error before attempting the mint.
      // Step 1: read which token the market pulls the seller-share in.
      logger.step("seller_token_read", { nftMarketAddress });
      const sellerToken = await publicClient.readContract({
        address: nftMarketAddress as `0x${string}`,
        abi: LAND_MARKET_ABI,
        functionName: "sellerToken",
      });

      // Step 2: fetch both token balances in parallel (we may need either one).
      logger.step("balance_check");
      const [uvmBalance, usdtBalance] = await Promise.all([
        publicClient.readContract({
          address: ContractAddress.UVM,
          abi: ERC20_ABI,
          functionName: "balanceOf",
          args: [address],
        }),
        publicClient.readContract({
          address: ContractAddress.USDT,
          abi: ERC20_ABI,
          functionName: "balanceOf",
          args: [address],
        }),
      ]);

      // Required per Payments.sol:37-83:
      //   sellerToken == 1 → USDT >= sv (6 dec)   AND   UVM >= bv * 1.03 (18 dec)
      //   sellerToken == 2 → UVM  >= (sv + bv * 1.03) in 18 dec
      const bvWei = parseEther(`${Number(price.bv)}`);
      const bvWithFee = (bvWei * BigInt(103)) / BigInt(100);
      const requiredUVM =
        sellerToken === 1
          ? bvWithFee
          : parseEther(`${Number(price.sv)}`) + bvWithFee;
      const requiredUSDT =
        sellerToken === 1 ? parseUnits(`${Number(price.sv)}`, 6) : BigInt(0);

      logger.step("balance_result", {
        sellerToken,
        usdtBalance: usdtBalance.toString(),
        uvmBalance: uvmBalance.toString(),
        requiredUSDT: requiredUSDT.toString(),
        requiredUVM: requiredUVM.toString(),
      });

      if (usdtBalance < requiredUSDT) {
        throw new InsufficientBalanceError(
          te("insufficientUSDT", {
            needed: formatTokenAmount(requiredUSDT - usdtBalance, 6),
          }),
        );
      }
      if (uvmBalance < requiredUVM) {
        throw new InsufficientBalanceError(
          te("insufficientUVM", {
            needed: formatTokenAmount(requiredUVM - uvmBalance, 18),
          }),
        );
      }

      // Mint NFT — log the exact contract inputs
      logger.step("nft_mint", {
        nftMarketAddress,
        tokenId,
        parentAddress,
        referrerAddress,
        sv: price.sv,
        bv: price.bv,
      });
      const mintTxHash = await writeContractAsync({
        address: nftMarketAddress as `0x${string}`,
        abi: LAND_MARKET_ABI,
        functionName: "mint",
        args: [
          [BigInt(tokenId)],
          ...landMarketMintArgs(
            parentAddress as `0x${string}`,
            referrerAddress as `0x${string}`,
          ),
        ] as never,
      });
      await waitAndVerifyTransaction(mintTxHash, "nft_mint");

      // Fetch user ID
      logger.step("fetch_user");
      const user = await publicClient.readContract({
        address: ContractAddress.GATEWAY,
        abi: GATEWAY_ABI,
        functionName: "getUser",
        args: [address],
      });

      // The wallet's ACTUAL on-chain referrer, read fresh post-tx -- not
      // whatever was typed into the form. V1 has no `referrer` field at all.
      const onChainReferrer =
        "referrer" in user ? (user.referrer as `0x${string}`) : undefined;

      const result = {
        tokenId,
        nftMarketAddress,
        userId: user.id,
        onChainReferrer,
      };

      logger.complete({
        tokenId,
        nftMarketAddress,
        userId: user.id.toString(),
      });

      return result;
    } catch (error) {
      logger.fail(error, {
        tokenId: params.tokenId,
        nftMarketAddress: params.nftMarketAddress,
        sv: params.price.sv,
        bv: params.price.bv,
      });
      throw error;
    }
  };

  return useMutation({
    mutationKey: ["purchase-nft", address],
    mutationFn,
    onSuccess: (data) => {
      // Always notify the backend on a successful NFT purchase so the user is
      // promoted from role=GUEST → USER and their _user_id / referer_address
      // are stored. Previously this call was wrapped in
      // `if (!variables.parentAddress)` — an inverted guard that fired the
      // mutation ONLY when the user did NOT enter a referrer, leaving everyone
      // who did enter one stuck in GUEST forever (dashboard layout then
      // redirects them to /standard-dashboard with no scores or tree).
      // See `useCheckoutPurchase.ts` for the sibling flow that already did this
      // unconditionally.
      //
      // Guard against sending `_user_id: 0`: data.userId is a bigint from
      // chain getUser(). When registration hasn't propagated yet (or the chain
      // read raced the placement tx), it can be 0n → Number(0n) = 0, which the
      // backend then persists verbatim. A stored `_user_id = 0` makes
      // user.service.ts:422 query `_parent_id = 0` (silent zero match) and
      // hides every pending referral purchase under that wallet. Skip the
      // notify and let the next purchase / login re-fire it once chain state
      // is fresh. Verified prod 2026-05-31: 8 wallets stuck this way; one
      // (0x03e1d3…1ed6c) hid 9 pending purchases.
      const userIdNum = Number(data.userId);
      if (!Number.isFinite(userIdNum) || userIdNum <= 0) {
        options?.onSuccess?.(data);
        return;
      }
      // Mirror the wallet's REAL on-chain referrer (read fresh post-tx), never
      // the typed form value — see useCheckoutPurchase.ts for the sibling
      // flow that fixed the same class of bug (it had been storing the tree
      // PARENT in this field instead of the referrer).
      const hasReferrer =
        !!data.onChainReferrer && data.onChainReferrer !== zeroAddress;
      updateUser.mutate({
        body: {
          usedRefererAddress: hasReferrer,
          referrerAddress: data.onChainReferrer ?? zeroAddress,
          _user_id: userIdNum,
        },
      });
      options?.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      // Show Persian error message to user
      handleContractError(error);

      // Call custom error handler if provided (with original error)
      if (options?.onError) {
        const errorObj =
          error instanceof Error ? error : new Error("Transaction failed");
        options.onError(errorObj);
      }
    },
  });
};
